import { useState, useEffect } from "react";
import axios from "axios";
import {
  Package, Plus, Edit3, Trash2, Loader2, RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestServiceOfficeSupply() {
  const auth = useAuth();
  const [items, setItems] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustQty, setAdjustQty] = useState(0);

  const [form, setForm] = useState({
    item_name: "",
    category: "",
    quantity: 0,
    min_threshold: 0,
    unit: "",
    notes: "",
  });

  const fetchItems = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [itemsRes, lowRes] = await Promise.all([
        axios.get(`${API}/request/supply`, { headers }),
        axios.get(`${API}/request/supply/low-stock`, { headers }),
      ]);
      setItems(itemsRes.data || []);
      setLowStockItems(lowRes.data || []);
    } catch (err) {
      toast.error("Failed to load office supply");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditItem(null);
    setForm({ item_name: "", category: "", quantity: 0, min_threshold: 0, unit: "", notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      item_name: item.item_name || "",
      category: item.category || "",
      quantity: item.quantity || 0,
      min_threshold: item.min_threshold || 0,
      unit: item.unit || "",
      notes: item.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      if (editItem) {
        await axios.put(`${API}/request/supply/${editItem.id}`, form, { headers });
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/request/supply`, form, { headers });
        toast.success("Item created");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save item");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/request/supply/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Item deleted");
      fetchItems();
    } catch (err) {
      toast.error("Failed to delete item");
    }
  };

  const handleAdjust = async () => {
    if (!adjustOpen.item) return;
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/request/supply/${adjustOpen.item.id}/adjust`,
        { quantity: adjustQty },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Stock adjusted");
      setAdjustOpen(false);
      setAdjustQty(0);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to adjust stock");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800">
          Office Supply
        </h1>
        <p className="text-slate-500 mt-1">Manage stationery, consumables and office supplies</p>
      </div>

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-700">
            {lowStockItems.length} item(s) below minimum threshold
          </p>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex items-center justify-between mb-4">
        <Button onClick={openCreate} className="bg-teal-500 hover:bg-teal-600">
          <Plus className="w-4 h-4 mr-2" /> Add Item
        </Button>
        <Button variant="outline" onClick={fetchItems}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-app">
              <th className="text-left p-4 font-semibold text-slate-600">Item</th>
              <th className="text-left p-4 font-semibold text-slate-600">Category</th>
              <th className="text-left p-4 font-semibold text-slate-600">Quantity</th>
              <th className="text-left p-4 font-semibold text-slate-600">Min Threshold</th>
              <th className="text-left p-4 font-semibold text-slate-600">Unit</th>
              <th className="text-left p-4 font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-slate-500">
                  No office supply items
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const isLow = item.quantity <= item.min_threshold;
                return (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-app">
                    <td className="p-4 font-medium text-slate-800">{item.item_name}</td>
                    <td className="p-4 text-slate-600">{item.category || "-"}</td>
                    <td className="p-4">
                      <span className={isLow ? "text-red-600 font-semibold" : "text-slate-800"}>
                        {item.quantity}
                      </span>
                      {isLow && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-500" />}
                    </td>
                    <td className="p-4 text-slate-600">{item.min_threshold}</td>
                    <td className="p-4 text-slate-600">{item.unit || "-"}</td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAdjustOpen({ item });
                            setAdjustQty(item.quantity);
                          }}
                        >
                          Edit3
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(item)}>
                          <Edit3 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editItem ? "Edit Item" : "Add Item"}</DialogTitle>
            <DialogDescription>
              {editItem ? "Update supply item details" : "Add a new supply item"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Item Name *</label>
              <Input value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Quantity</label>
                <Input
                  type="number"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Min Threshold</label>
                <Input
                  type="number"
                  value={form.min_threshold}
                  onChange={(e) => setForm({ ...form, min_threshold: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Unit</label>
              <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="e.g. box, ream, piece" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <Button onClick={handleSave} className="bg-teal-500 hover:bg-teal-600 w-full">
              {editItem ? "Update" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog
        open={!!adjustOpen.item}
        onOpenChange={(o) => { if (!o) setAdjustOpen(false); }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
            <DialogDescription>Update quantity for {adjustOpen.item?.item_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="number"
              value={adjustQty}
              onChange={(e) => setAdjustQty(parseInt(e.target.value) || 0)}
            />
            <Button onClick={handleAdjust} className="bg-teal-500 hover:bg-teal-600 w-full">
              Update
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
